import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo } from '@/mocks/siteInfo';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 前列腺健康知识科普',
  description: siteInfo.description,
  url: 'https://prostate-health-guide.readdy.ai/about',
  mainEntity: {
    '@type': 'Organization',
    name: siteInfo.name,
    description: siteInfo.description,
    foundingDate: siteInfo.founded,
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteInfo.contact.email,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  },
};

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }
    const formData = new FormData(form);
    formData.delete('phone_alt');
    const urlSearchParams = new URLSearchParams();
    formData.forEach((value, key) => {
      urlSearchParams.append(key, value as string);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d9sr4g94ne17ei0sdmh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlSearchParams.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description={siteInfo.description}
        keywords="前列腺健康科普,关于我们,男性健康,健康科普平台"
        canonicalUrl="https://prostate-health-guide.readdy.ai/about"
        ogType="website"
        schema={aboutSchema}
      />

      <div className="pt-16">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
        </div>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-6">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto mb-10">
              {siteInfo.description}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {siteInfo.socialProof.map((item) => (
                <div key={item.label} className="bg-background-100 rounded-lg p-4 md:p-5">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-1">
                    {item.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
              我们的核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {siteInfo.values.map((v) => (
                <div key={v.title} className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mx-auto mb-4">
                    <i className={`${v.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
              我们的团队
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {siteInfo.team.map((member) => (
                <div key={member.name} className="bg-background-100 rounded-lg p-5 md:p-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mx-auto mb-4">
                    <i className="ri-user-star-line text-2xl"></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 text-center mb-1">
                    {member.name}
                  </h3>
                  <p className="text-xs text-primary-600 text-center mb-3">{member.role}</p>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed text-center">
                    {member.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-4">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              如有任何问题或建议，欢迎通过以下表单与我们取得联系
            </p>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8 space-y-5"
            >
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言（不超过500字）"
                ></textarea>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md font-medium text-sm transition-colors whitespace-nowrap cursor-pointer"
              >
                提交留言
              </button>

              {formState === 'success' && (
                <p className="text-sm text-primary-600 text-center">感谢您的留言，我们会尽快回复！</p>
              )}
              {formState === 'error' && (
                <p className="text-sm text-accent-600 text-center">{formError}</p>
              )}
            </form>

            <div className="mt-8 text-center">
              <p className="text-sm text-foreground-500">
                邮箱：{siteInfo.contact.email}
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}